import { escapeHtmlAttr } from '@shared/utils/helpers';

/**
 * Represents a content entry returned by the ContentController.GetContentByGroup endpoint.
 */
export interface ContentEntry {
    Id: string;
    Title: string;
    TeaserText: string;
    MediaURL: string | null;
    ImageAltText: string | null;
    UserFriendlyUrl: string | null;
}

/**
 * Configuration options read from the data attributes of the news grid container element.
 */
export interface NewsGridConfig {
    /** Maximum number of news articles to display. */
    maxItems: number;
    /** The content group ID to fetch articles for. */
    groupId: string;
    /** URL of the GetContentByGroup API endpoint. */
    apiUrl: string;
    /** Base URL for the content detail page (e.g. /Content/Index). */
    contentBaseUrl: string;
}

/**
 * Manages a dynamic news grid on the home page.
 * Reads configuration from the container's data attributes, fetches content entries
 * from the ContentController endpoint and renders news cards into the grid.
 */
export class NewsGridManager {
    private readonly container: HTMLElement;
    private readonly config: NewsGridConfig;

    /**
     * @param container - The DOM element that wraps the news grid and holds data attributes.
     */
    constructor(container: HTMLElement) {
        this.container = container;
        this.config = this.readConfig();
        this.initialize();
    }

    /**
     * Reads the data attributes from the container element and builds the configuration object.
     *
     * @returns The parsed configuration for this news grid instance.
     */
    private readConfig(): NewsGridConfig {
        const dataset = this.container.dataset;

        return {
            maxItems: parseInt(dataset.maxItems ?? '3', 10),
            groupId: dataset.groupId ?? '',
            apiUrl: dataset.apiUrl ?? '/Content/GetContentByGroup',
            contentBaseUrl: dataset.contentBaseUrl ?? '/Content/Index',
        };
    }

    /**
     * Initialises the component: renders a skeleton loader, fetches data and then renders the result.
     *
     * @returns A promise that resolves when initialisation is complete.
     */
    private async initialize(): Promise<void> {
        this.renderSkeleton();

        try {
            const entries = await this.fetchEntries();
            this.renderEntries(entries);
        } catch (error) {
            console.error('[NewsGrid] Failed to load news entries:', error);
            this.renderError();
        }
    }

    /**
     * Fetches content entries from the ContentController.GetContentByGroup endpoint.
     *
     * @returns A promise resolving to an array of ContentEntry objects (limited to maxItems).
     */
    private async fetchEntries(): Promise<ContentEntry[]> {
        const params = new URLSearchParams({
            groupId: this.config.groupId,
        });

        const response = await fetch(`${this.config.apiUrl}?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            throw new Error(`API responded with status ${response.status}`);
        }

        const allEntries = await response.json() as ContentEntry[];

        // Limit the result to the configured maxItems count
        return allEntries.slice(0, this.config.maxItems);
    }

    /**
     * Renders skeleton placeholder cards while the real data is being fetched.
     * The number of skeletons matches the configured maxItems value.
     */
    private renderSkeleton(): void {
        const grid = this.container.querySelector<HTMLElement>('.news-grid-items');
        if (!grid) return;

        grid.innerHTML = '';

        for (let i = 0; i < this.config.maxItems; i++) {
            const card = document.createElement('div');
            card.className = 'bg-[#f7f7f8] border border-[#eaeced] flex flex-1 flex-col items-start min-w-0 overflow-hidden relative rounded-2xl animate-pulse';
            card.innerHTML = `
                <div class="h-[233px] w-full bg-gray-200"></div>
                <div class="flex flex-col gap-6 items-start justify-center p-8 w-full">
                    <div class="flex flex-col gap-4 w-full">
                        <div class="h-8 bg-gray-200 rounded w-3/4"></div>
                        <div class="h-4 bg-gray-200 rounded w-full"></div>
                        <div class="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                    <div class="h-5 bg-gray-200 rounded w-1/4"></div>
                </div>`;
            grid.appendChild(card);
        }
    }

    /**
     * Renders news cards from the fetched data into the grid element.
     *
     * @param entries - The list of content entries to render.
     */
    private renderEntries(entries: ContentEntry[]): void {
        const grid = this.container.querySelector<HTMLElement>('.news-grid-items');
        if (!grid) return;

        grid.innerHTML = '';

        if (entries.length === 0) {
            this.renderEmpty(grid);
            return;
        }

        entries.forEach(entry => {
            const card = this.createNewsCard(entry);
            grid.appendChild(card);
        });
    }

    /**
     * Builds the URL for the content detail page.
     * Uses UserFriendlyUrl if available, otherwise falls back to the entry's Id.
     *
     * @param entry - The content entry to build the URL for.
     * @returns The URL string pointing to the content detail page.
     */
    private buildDetailUrl(entry: ContentEntry): string {
        const slug = entry.UserFriendlyUrl ?? entry.Id;
        return `${this.config.contentBaseUrl}?slug=${encodeURIComponent(slug)}`;
    }

    /**
     * Creates a single news card element matching the existing static news card design.
     *
     * @param entry - The content entry data to render.
     * @returns A fully constructed HTMLElement representing the card.
     */
    private createNewsCard(entry: ContentEntry): HTMLElement {
        const card = document.createElement('div');
        card.className = 'bg-[#f7f7f8] border border-[#eaeced] flex flex-1 flex-col items-start min-w-0 overflow-hidden relative rounded-2xl';

        const detailUrl = this.buildDetailUrl(entry);

        // Only render the image section when a MediaURL is available
        const hasImage = !!entry.MediaURL;
        const imageHtml = hasImage
            ? `<div class="flex flex-col h-[233px] items-center justify-end relative w-full">
                   <div class="flex-1 min-h-0 min-w-0 relative w-full">
                       <img src="${escapeHtmlAttr(entry.MediaURL!)}"
                            alt="${escapeHtmlAttr(entry.ImageAltText ?? entry.Title)}"
                            class="absolute inset-0 w-full h-full object-cover"
                            loading="lazy">
                   </div>
               </div>`
            : '';

        // TeaserText may contain HTML and must be rendered as raw HTML
        const teaserHtml = entry.TeaserText
            ? `<div class="font-['Asap'] font-normal text-[16px] leading-[1.3] tracking-[-0.16px] w-full news-teaser-text">${entry.TeaserText}</div>`
            : '';

        card.innerHTML = `
            ${imageHtml}
            <div class="flex flex-col gap-6 items-start justify-center p-8 w-full">
                <div class="flex flex-col items-start w-full">
                    <div class="flex flex-col gap-4 items-start text-[#303e48] w-full">
                        <h3 class="font-['Asap'] font-medium text-[32px] leading-[1.3] tracking-[-0.32px] w-full">
                            ${escapeHtmlAttr(entry.Title)}
                        </h3>
                        ${teaserHtml}
                    </div>
                </div>
                <div class="flex items-center w-full">
                    <a href="${escapeHtmlAttr(detailUrl)}"
                       class="flex gap-2 items-center text-company font-['Asap'] font-medium text-[16px] leading-[1.3] tracking-[-0.16px] hover:text-company-hover transition-colors">
                        Weiter lesen
                        <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5l8 7-8 7"/></svg>
                    </a>
                </div>
            </div>`;

        return card;
    }

    /**
     * Renders an empty-state message when the API returns no entries.
     *
     * @param grid - The grid container element to render the message into.
     */
    private renderEmpty(grid: HTMLElement): void {
        grid.innerHTML = `
            <p class="font-['Asap'] text-[#646e76] text-[16px] col-span-full py-8 text-center w-full">
                Keine Neuigkeiten verfügbar.
            </p>`;
    }

    /**
     * Renders an error message inside the grid when the API call fails.
     */
    private renderError(): void {
        const grid = this.container.querySelector<HTMLElement>('.news-grid-items');
        if (!grid) return;

        grid.innerHTML = `
            <p class="font-['Asap'] text-red-500 text-[16px] col-span-full py-8 text-center w-full">
                Die News konnten nicht geladen werden. Bitte versuche es später erneut.
            </p>`;
    }
}

